import axios from 'axios';
import * as Sentry from '@sentry/browser';

import { RECAPTCHA_SITE_KEY, VERSION } from 'shared/constants';
import {
  getApiUrl,
  getRegistrationToken,
  getStorageItem,
  getToken,
  removeRegistrationToken,
  removeToken,
} from 'utils/asyncStorage';
import { getLanguage } from '$translation';
import { goTo } from '$navigation/helper';
import {
  BIND_EMAIL_CONFIRM,
  COMMON_FEED,
  PROFILE,
  PROMO_CODE,
  RESOLVE,
  SUBSCRIPTION_ADULT,
  SUBSCRIPTION_AUTHOR,
  SUBSCRIPTION_MEDIA,
  VALENTINE_DAY_STICKER,
} from '$shared/constants/pages';
import { SubscriptionType } from '$store/models';
import router from '$navigation/router';
import { _prompt, getCaptchaToken, statReachGoal } from '$utils';
import {
  authorizationCode,
  authorizationDefault,
  requestAuthorization,
} from '$passkey/authorization';
import { PasskeyRedirect, apiMessage } from '$passkey/http';
import { t } from '$translation/helper';
import { isUploadLocked } from '$utils/uploadLock';

const delay = (ms = 0) => new Promise((resolve) => setTimeout(resolve, ms));

export const Api = axios.create({
  baseURL: getApiUrl(),
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept-Language': getLanguage(),
  },
});

const parseHexToInt = (hex?: string | null): number => {
  if (!hex) return 0;
  const result = Number.parseInt(hex, 16);
  return isNaN(result) ? 0 : result;
};

const getRef = () => parseHexToInt(getStorageItem('ref'));
const getTimezone = () => new Date().getTimezoneOffset();

const isTelegramMiniApp = () => {
  try {
    const hash = (window as any)?.Telegram?.WebApp?.initDataUnsafe?.hash;
    return typeof hash === 'string' && hash.length > 0;
  } catch {
    return false;
  }
};

const getInitiator = () => (isTelegramMiniApp() ? 'ma_prod' : 'web_prod');

const XUserIDHeader = 'X-User-ID';

Api.interceptors.request.use(
  (config) => {
    let { token, userId } = getToken();
    if (!token && config.url === '/registration') {
      token = getRegistrationToken() ?? '';
    }

    const nextConfig = {
      ...config,
      baseURL: getApiUrl(),
      headers: {
        ...config.headers,
        Authorization: token || '',
        Version: VERSION,
        Timezone: getTimezone(),
        Ref: getRef(),
        Initiator: getInitiator(),
        [XUserIDHeader]: userId ? String(userId) : 'unknown',
      },
    };

    try {
      Sentry.addBreadcrumb({
        category: 'http',
        type: 'http',
        level: 'info',
        data: { method: nextConfig.method, url: nextConfig.url },
      });
    } catch {}

    return nextConfig;
  },
  (error) => {
    return Promise.reject(error);
  },
);

let version = 0;
let reloadPending = false;

Api.interceptors.response.use(
  (response) => {
    if (['document', 'text'].indexOf(response.config.responseType as string) > -1) {
      return response.data;
    }

    if (response.headers.version) {
      const newVersion = parseFloat(response.headers.version);
      if (version && newVersion > version) {
        reloadPending = true;
      }
      version = newVersion;
    }
    // Same moment as before (an API response), just not while a video upload modal is open.
    if (reloadPending && !isUploadLocked()) {
      // @ts-ignore
      location.reload(true);
    }
    return response.status === 200
      ? Promise.resolve(response.data)
      : Promise.reject(response.data);
  },
  async (error) => {
    try {
      Sentry.setTag('api.status', String(error?.response?.status ?? 'n/a'));
      Sentry.setTag('api.code', String(error?.response?.data?.code ?? 'n/a'));
      Sentry.setContext('request', {
        url: error?.config?.url,
        method: error?.config?.method,
      });
    } catch {}

    if (error.response?.status === 428) {
      const action = 'action_' + Date.now();
      const rVersion = error.response.headers['g-recaptcha-version'];
      let token = '';

      if (rVersion === 'V2') {
        try {
          token = await getCaptchaToken();
        } catch (e) {
          console.warn('[api:captcha] failed to obtain V2 token');
          try {
            Sentry.addBreadcrumb({
              category: 'auth',
              level: 'warning',
              message: 'captcha_v2_failed',
            });
          } catch {}
          return Promise.reject({
            message: 'Captcha entered incorrectly',
          });
        }
      } else {
        // @ts-ignore
        // eslint-disable-next-line no-undef
        token = await grecaptcha.execute(RECAPTCHA_SITE_KEY, {
          action,
        });
      }

      return Api.request({
        ...error.config,
        headers: {
          ...error.config.headers,
          'g-recaptcha-action': action,
          'g-recaptcha-version': rVersion,
          'g-recaptcha-token': token,
        },
      });
    }

    const skipSentry = Boolean(error.config?.skipSentry);

    if (error.response?.status >= 500) {
      console.error('[api:error] server', { status: error.response?.status });
      try {
        if (!skipSentry) Sentry.captureException(error);
      } catch {}
      await delay(500);
      return Promise.reject({
        message: 'Server error',
        serverError: true,
      });
    }

    if (error.message === 'Network Error' || !error.response) {
      // statReachGoal('network_error'); // вызывал залуп если пропал интернет
      console.warn('[api:error] network');
      try {
        if (!skipSentry) Sentry.captureException(error);
      } catch {}
      await delay(500);
      return Promise.reject({
        message: 'Ошибка сети',
        isNetworkError: true,
      });
    } else if (error.response.data?.code === 'access_token') {
      removeToken();
      removeRegistrationToken();
      const { name } = router.getState();

      if (
        ![
          RESOLVE,
          PROFILE,
          COMMON_FEED,
          SUBSCRIPTION_AUTHOR,
          PROMO_CODE,
          VALENTINE_DAY_STICKER,
          BIND_EMAIL_CONFIRM,
        ].includes(name)
      ) {
        location.href =
          '/auth_required?target=' +
          encodeURIComponent(location.pathname + location.search);
      }
    } else if (error.response.data?.code === 'blocked') {
      removeToken();
      location.href = '/blocked';
    } else if (error.response.data?.code === 'country_blocked') {
      location.href = '/country_blocked';
    } else if (error.response.data?.code === 'profile_country_blocked') {
      // skip
    } else if (error.response.data?.code === 'subscription') {
      if (error.response.data?.params?.subscriptionType === SubscriptionType.Adult) {
        goTo(SUBSCRIPTION_ADULT);
      } else {
        goTo(SUBSCRIPTION_MEDIA);
      }
    } else if (error.response.data?.code === 'personal_subscription') {
      goTo(SUBSCRIPTION_AUTHOR, {
        authorId: error.response.data?.params.authorId,
      });
    } else if (error.response.data?.code === 'confirmation_required') {
      const confirmCode = await _prompt({
        title: t('confirm_code_title'),
        description: t('confirm_code_description'),
        placeholder: t('confirm_code_input_placeholder'),
      });

      if (confirmCode) {
        return Api.request({
          ...error.config,
          data: JSON.stringify({
            ...JSON.parse(error.config.data),
            confirmCode,
          }),
        });
      }

      return Promise.reject({
        message: 'Code is required',
      });
    } else if (error.response.data?.code === 'webauthn_required') {
      let ceremonyId = '';
      try {
        ceremonyId = await requestAuthorization(error.response.data?.params);
      } catch (e) {
        console.warn('[api:webauthn] authorization failed');
        if (e instanceof PasskeyRedirect) {
          return Promise.reject({ code: e.code, message: e.message });
        }
        const code = authorizationCode(e);
        if (code) {
          return Promise.reject({
            code,
            message: t(code, { defaultValue: authorizationDefault(code) }),
          });
        }
        if (apiMessage(e)) return Promise.reject(e);
        return Promise.reject({
          code: 'passkey_auth_error',
          message: t('passkey_auth_error', {
            defaultValue: authorizationDefault('passkey_auth_error'),
          }),
        });
      }

      return Api.request({
        ...error.config,
        headers: {
          ...error.config.headers,
          'X-PassKey-Token': ceremonyId,
        },
      });
    } else {
      statReachGoal('api_error', {
        error: error.response ? error.response.data : error,
      });
      console.error('[api:error] unexpected', {
        code: error.response?.data?.code,
        status: error.response?.status,
      });
      try {
        Sentry.captureException(error);
      } catch {}
    }

    return Promise.reject(error.response ? error.response.data : error);
  },
);
